import React, { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import Emoji from "@tiptap/extension-emoji";
import CodeBlock from "@tiptap/extension-code-block";
import { Extension } from "@tiptap/core";

import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Tooltip,
  Popper,
  ClickAwayListener,
  Tabs,
  Tab,
  Paper,
  Typography,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  InsertPhoto,
  Code,
  FormatClear,
  Undo,
  Redo,
  InsertEmoticon,
  Delete,
  ContentCopy,
} from "@mui/icons-material";
import { MentionExtension } from "./Mention";
import { useSelector } from "react-redux";
import { TextSelection } from "prosemirror-state";
import { useFileUploads } from "../Discussion/discussionHooks/useFileUploads";
import { setSelectedMentionsUsers } from "../../features/genericCommentsAndFilesSlice";
import { useDispatch } from "react-redux";
import { CustomImage } from "./editorCustomExtentions/CustomImageExtension";
import { FileExtension } from "./editorCustomExtentions/FileExtension";
import { validateUrl } from "../../utils/helpers";
import CharacterCountLimit from "./editorCustomExtentions/CharacterCountLimit";
import { setDisplayErrorSnackBar } from "../../features/userSlice";

const MenuBar = ({ editor }: any) => {
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState("");
  const [imageUrl, setImageUrl] = React.useState("");
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const emojiButtonRef = React.useRef<HTMLButtonElement>(null);
  const [imageTab, setImageTab] = React.useState<"file" | "link">("file");
  const [imageUrlError, setImageUrlError] = React.useState("");
  const [linkError, setLinkError] = useState(""); // for showing validation error
  const { processFiles } = useFileUploads();

  const setLink = useCallback(() => {
    if (!editor) return;
    if (!linkUrl) return;

    // Check for empty = remove link
    if (linkUrl.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setLinkDialogOpen(false);
      setLinkUrl("");
      setLinkError("");
      return;
    }

    // Validate URL format
    if (!validateUrl(linkUrl)) {
      setLinkError("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: linkUrl })
      .run();
    setLinkDialogOpen(false);
    setLinkUrl("");
    setLinkError("");
  }, [editor, linkUrl]);

  const handleLinkOpen = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    setLinkUrl(previousUrl || "");
    setLinkDialogOpen(true);
    setLinkError(""); // clear any previous error
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
          borderBottom: "1px solid #eee",
          pb: 1,
        }}
      >
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Undo">
            <IconButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
            >
              <Undo fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Redo">
            <IconButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
            >
              <Redo fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small" variant="text">
          <Tooltip title="Heading">
            <Select
              color="secondary"
              size="small"
              variant="standard"
              value={
                editor.isActive("heading")
                  ? `h${editor.getAttributes("heading").level}`
                  : "paragraph"
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === "paragraph") {
                  editor.chain().focus().setParagraph().run();
                } else {
                  const level = parseInt(value.replace("h", ""));
                  editor.chain().focus().toggleHeading({ level }).run();
                }
              }}
              sx={{
                minWidth: 120,
                fontSize: "0.875rem",
                marginLeft: 1,
              }}
            >
              <MenuItem value="paragraph">Paragraph</MenuItem>
              <MenuItem value="h1">Heading 1</MenuItem>
              <MenuItem value="h2">Heading 2</MenuItem>
              <MenuItem value="h3">Heading 3</MenuItem>
            </Select>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small" variant="text">
          <Tooltip title="Bold">
            <IconButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              color={editor.isActive("bold") ? "secondary" : "default"}
            >
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              color={editor.isActive("italic") ? "secondary" : "default"}
            >
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              color={editor.isActive("underline") ? "secondary" : "default"}
            >
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title="Strikethrough">
            <IconButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              color={editor.isActive("strike") ? "secondary" : "default"}
            >
              <StrikethroughS fontSize="small" />
            </IconButton>
          </Tooltip> */}
          {/* This for Code Block */}

          <Tooltip title="Code snippet ```">
            <IconButton
              // onClick={() => {
              //   editor
              //     .chain()
              //     .focus()
              //     .command(({ tr, state, dispatch }: any) => {
              //       const { selection } = state;
              //       const { from, to, empty } = selection;
              //       const { codeBlock, paragraph } = state.schema.nodes;

              //       if (!empty) {
              //         // 🔁 Text is selected → wrap in codeBlock
              //         tr.setBlockType(from, to, codeBlock);
              //         dispatch(tr);
              //         return true;
              //       }

              //       // ➕ No text selected → insert new codeBlock + paragraph after current block
              //       const { $from } = selection;
              //       const endPos = $from.end($from.depth);

              //       tr.insert(endPos, [
              //         codeBlock.create(),
              //         paragraph.create(), // optional: insert paragraph after codeBlock
              //       ]);
              //       dispatch(tr);
              //       return true;
              //     })
              //     .run();
              // }}
              onClick={() => {
                const { selection } = editor.state;
                const { from, to, empty } = selection;

                if (!empty) {
                  // Text selected - wrap in code block with proper paragraphs
                  const selectedText = editor.state.doc.textBetween(from, to);

                  editor
                    .chain()
                    .focus()
                    .deleteSelection()
                    .insertContent([
                      { type: "paragraph" }, // Empty paragraph above
                      {
                        type: "codeBlock",
                        content: [{ type: "text", text: selectedText }],
                      },
                      { type: "paragraph" }, // Empty paragraph below
                    ])
                    .run();

                  // Position cursor after code block
                  setTimeout(() => {
                    const newPos = from + 2; // Adjust for new structure
                    editor.commands.setTextSelection(newPos);
                  }, 0);
                } else {
                  // No selection - insert code block with paragraphs
                  editor
                    .chain()
                    .focus()
                    .insertContent([
                      { type: "paragraph" },
                      { type: "codeBlock" },
                      { type: "paragraph" },
                    ])
                    .run();

                  // Focus inside code block
                  setTimeout(() => {
                    const pos = editor.state.selection.from + 1;
                    editor.commands.setTextSelection(pos);
                  }, 0);
                }
              }}
              color={editor.isActive("codeBlock") ? "secondary" : "default"}
            >
              <Code fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small" variant="text">
          <Tooltip title="Bullet List">
            <IconButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              color={editor.isActive("bulletList") ? "secondary" : "default"}
            >
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ordered List">
            <IconButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              color={editor.isActive("orderedList") ? "secondary" : "default"}
            >
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small" variant="text">
          <Tooltip title="Link">
            <IconButton
              onClick={handleLinkOpen}
              color={editor.isActive("link") ? "secondary" : "default"}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add image or file">
            <IconButton
              onClick={() => {
                setImageTab("file"); // <- reset to "file" tab every time
                setImageDialogOpen(true);
              }}
            >
              <InsertPhoto fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small" variant="text">
          <Tooltip title="Clear Format">
            <IconButton
              onClick={() =>
                editor.chain().focus().clearNodes().unsetAllMarks().run()
              }
            >
              <FormatClear fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        {/* This Part is for Emoji */}
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Emoji">
            <IconButton
              ref={emojiButtonRef}
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              <InsertEmoticon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>

        <Popper
          open={showEmojiPicker}
          anchorEl={emojiButtonRef.current}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
            <Box sx={{ zIndex: 1500 }}>
              <Picker
                data={data}
                onEmojiSelect={(emoji: any) => {
                  editor.chain().focus().insertContent(emoji.native).run();
                  setShowEmojiPicker(false);
                }}
                theme="light"
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>

      {/* Link Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="standard"
            value={linkUrl}
            onChange={(e) => {
              const value = e.target.value;
              setLinkUrl(e.target.value);
              // Live validation for enabling Apply button
              if (value === "" || validateUrl(value)) {
                setLinkError("");
              } else {
                setLinkError(
                  "Please enter a valid URL (e.g., https://example.com)"
                );
              }
            }}
            placeholder="Paste link to upload"
            color="secondary"
            error={!!linkError}
            helperText={linkError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={setLink}
            color="secondary"
            variant="contained"
            disabled={!linkUrl || Boolean(linkError)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => {
          setImageDialogOpen(false);
          setImageUrlError(""); // reset error
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add File</DialogTitle>

        <DialogContent>
          {imageTab === "file" && imageUrlError && (
            <Typography color="error" mt={1} whiteSpace="pre-line">
              {imageUrlError}
            </Typography>
          )}
          <Tabs
            indicatorColor="secondary"
            value={imageTab}
            onChange={(_, newValue: any) => {
              setImageUrlError("");
              setImageTab(newValue);
              setImageUrl("");
            }}
            aria-label="Image upload type"
            textColor="secondary"
          >
            <Tab color="secondary" label="File" value="file" />
            <Tab color="secondary" label="Link" value="link" />
          </Tabs>

          {imageTab === "file" && (
            <Box mt={2}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "*/*"; //Allow all file types
                  input.multiple = true;

                  input.onchange = () => {
                    const files = Array.from(input.files || []);
                    const allowedTypes = [
                      "image/png",
                      "image/jpeg",
                      "application/pdf",
                    ];
                    const maxSize = 5 * 1024 * 1024; // 5 MB
                    const invalidFiles = files.filter(
                      (file) =>
                        !allowedTypes.includes(file.type) || file.size > maxSize
                    );

                    if (invalidFiles.length > 0) {
                      const errorMessages = invalidFiles.map((file) => {
                        if (!allowedTypes.includes(file.type)) {
                          return `${file.name}: Unsupported file type. Only PNG, JPEG, and PDF allowed(Max size: 5MB).`;
                        } else if (file.size > maxSize) {
                          return `${file.name}: File size exceeds 5MB`;
                        }
                        return null;
                      });

                      // Show error inside popup using state
                      setImageUrlError(errorMessages.join("\n"));
                      return; // Don't close dialog
                    }

                    processFiles(files, "fromEditor"); // handles base64 conversion + store redux + upload to db

                    files.forEach((file) => {
                      const url = URL.createObjectURL(file);
                      const filename = file.name;
                      const filetype =
                        filename.split(".").pop()?.toLowerCase() || "file";
                      const isImage = file.type.startsWith("image/");

                      if (isImage) {
                        editor
                          .chain()
                          .focus()
                          .insertContent([
                            {
                              type: "image",
                              attrs: {
                                src: url,
                                filename,
                              },
                            },
                            { type: "paragraph" },
                          ])
                          .run();
                      } else {
                        editor
                          .chain()
                          .focus()
                          .insertContent([
                            {
                              type: "file", //Insert custom file node
                              attrs: {
                                src: url,
                                filename,
                                filetype,
                                uploadedAt: new Date().toISOString(),
                              },
                            },
                            { type: "paragraph" },
                          ])
                          .run();
                      }

                      // Move cursor to end after insert
                      const tr = editor.state.tr;
                      const endPos = tr.doc.content.size;
                      tr.setSelection(TextSelection.create(tr.doc, endPos));
                      editor.view.dispatch(tr);
                      editor.view.focus();
                    });

                    setImageDialogOpen(false); //Close dialog
                    setImageUrlError(""); //Clear error
                  };

                  input.click();
                }}
              >
                Upload Images
              </Button>
            </Box>
          )}

          {/* Show image URL input for link tab */}
          {imageTab === "link" && (
            <TextField
              color="secondary"
              autoFocus
              margin="dense"
              label="Image URL"
              type="url"
              fullWidth
              variant="outlined"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setImageUrlError("");
              }}
              error={Boolean(imageUrlError)}
              helperText={imageUrlError}
              placeholder="Paste link to upload"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>

        {/* Show actions only for link tab */}
        {imageTab === "link" && (
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setImageDialogOpen(false);
                setImageUrlError("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                try {
                  setImageUrlError(""); //Reset error initially
                  const parsedUrl = new URL(imageUrl);
                  const parts = parsedUrl.pathname.split("/");
                  const filename = parts[parts.length - 1] || "file";
                  const extension =
                    filename.split(".").pop()?.toLowerCase() || "";
                  const filetype = extension;

                  // const isImage = /\.(png|jpe?g|gif|bmp|svg|webp)$/i.test(
                  //   filename
                  // );

                  const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
                  //const maxSize = 5 * 1024 * 1024; // 5 MB

                  // Validate extension
                  if (!allowedExtensions.includes(extension)) {
                    setImageUrlError(
                      `${filename}: Unsupported file type. Only PNG, JPEG, and PDF allowed (Max size: 5MB).`
                    );
                    return;
                  }

                  if (/\.(png|jpe?g)$/i.test(filename)) {
                    // Validate image load
                    const img = new window.Image();
                    img.onload = () => {
                      editor
                        .chain()
                        .focus()
                        .insertContent([
                          {
                            type: "image",
                            attrs: { src: imageUrl, filename },
                          },
                          { type: "paragraph" },
                        ])
                        .run();
                      setImageUrl("");
                      setImageUrlError("");
                      setImageDialogOpen(false);
                    };
                    img.onerror = () =>
                      setImageUrlError(
                        "Invalid image URL. Please check and try again."
                      );
                    img.src = imageUrl;
                    return;
                  }
                  if (extension === "pdf") {
                    // ✅ Insert custom file node
                    editor
                      .chain()
                      .focus()
                      .insertContent([
                        {
                          type: "file",
                          attrs: {
                            src: imageUrl,
                            filename,
                            filetype,
                            uploadedAt: new Date().toISOString(),
                          },
                        },
                        { type: "paragraph" },
                      ])
                      .run();

                    setImageUrl("");
                    setImageUrlError("");
                    setImageDialogOpen(false);
                    return;
                  }

                  setImageUrlError(
                    "Unsupported file type. Only PNG, JPEG, PDF and Max size: 5MB allowed."
                  );
                } catch (err) {
                  setImageUrlError("Invalid URL format.");
                }
              }}
              disabled={!imageUrl.trim() || Boolean(imageUrlError)}
            >
              Insert
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Heading Dialog */}
    </>
  );
};

const TiptapEditor = ({
  content,
  onUpdate,
  placeholder,
  editable,
  style,
  autoFocus,
}: any) => {
  const dispatch = useDispatch();
  const allUsers = useSelector((state: any) => state.user.allUsers);
  const [selectedImageElement, setSelectedImageElement] =
    useState<HTMLElement | null>(null);

  const uploadImage = async (file: File): Promise<string> => {
    // Replace this with your real upload logic
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return base64;
  };

  const editor = useEditor({
    editable: editable ?? true, // default: true, while loading then false
    extensions: [
      CharacterCountLimit.configure({
        limit: 1000,
        onLimitReached: () => {
          dispatch(
            setDisplayErrorSnackBar({
              message: "You've reached the 1000 character limit.",
              type: "error",
            })
          );
        },
      }),
      StarterKit.configure({ codeBlock: false }),
      FileExtension, // Custom file extension
      CodeBlock.configure({
        HTMLAttributes: {
          class: "my-custom-code-block",
          style: "margin-bottom: 8px; margin-top: 8px;",
        },
        exitOnTripleEnter: false, // Exit code block with 3 enters
        exitOnArrowDown: false, // Exit when pressing arrow down at end
      }),
      Extension.create({
        name: "codeBlockNavigation",

        addKeyboardShortcuts() {
          return {
            "Mod-Shift-c": () => {
              // Your code block insertion logic here
              return true;
            },

            // Click simulation for cursor positioning
            Escape: ({ editor }) => {
              if (editor.isActive("codeBlock")) {
                const { selection } = editor.state;
                const { $from } = selection;
                const pos = $from.after($from.depth);

                // Ensure there's a paragraph after code block
                const nextNode = editor.state.doc.nodeAt(pos);
                if (!nextNode || nextNode.type.name !== "paragraph") {
                  editor
                    .chain()
                    .insertContentAt(pos, { type: "paragraph" })
                    .setTextSelection(pos + 1)
                    .run();
                } else {
                  editor.commands.setTextSelection(pos + 1);
                }
                return true;
              }
              return false;
            },
          };
        },

        // Add commands for programmatic cursor positioning
        addCommands() {
          return {
            positionCursorAboveCodeBlock:
              (pos: any) =>
              ({ commands }: any) => {
                return commands.insertContentAt(pos, { type: "paragraph" });
              },

            positionCursorBelowCodeBlock:
              (pos: any) =>
              ({ commands }: any) => {
                return commands.insertContentAt(pos, { type: "paragraph" });
              },
          } as any;
        },
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        autolink: false,
        linkOnPaste: true,
      }),
      CustomImage,
      Placeholder.configure({
        placeholder: ({ editor }) =>
          !editor.isEmpty ? "" : placeholder || "Start typing...",
      }),
      Emoji.configure({
        enableEmoticons: true, // allows :) and :D to work
      }),
      MentionExtension,
    ],
    content,
    onUpdate: ({ editor }) => {
      // Get HTML content
      const html = editor.getHTML();
      //Get mentions users ids
      const mentionIdSet = new Set<string>();

      editor.state.doc.descendants((node) => {
        if (node.type.name === "mention") {
          mentionIdSet.add(node.attrs.id);
        }
      });

      const uniqueMentionIds = Array.from(mentionIdSet);

      if (uniqueMentionIds.length > 0) {
        dispatch(setSelectedMentionsUsers(uniqueMentionIds)); //store the mentions users ids
      }
      //Propagate update to parent
      onUpdate?.(html);
    },
    onCreate({ editor }) {
      editor.storage.userList =
        allUsers?.length > 0 &&
        allUsers.map((user: any) => ({
          id: user.id,
          label: `${user.first_name} ${user.last_name}`,
          avatar:
            user.avatar_url ||
            `https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}`,
          fullName: `${user.first_name} ${user.last_name}`, // (optional) keep full user object if needed
        }));
    },
    editorProps: {
      handleDrop(view, event, _slice, _moved) {
        const hasFiles = event.dataTransfer?.files?.length;

        if (!hasFiles) return false;

        const images = Array.from(event.dataTransfer.files).filter((file) =>
          /image/i.test(file.type)
        );

        if (images.length === 0) return false;

        event.preventDefault();

        images.forEach(async (image) => {
          const imageUrl = await uploadImage(image);
          const filename = image.name || "file";

          const { schema } = view.state;
          const node = schema.nodes.image.create({
            src: imageUrl,
            filename: filename,
          });
          const transaction = view.state.tr.replaceSelectionWith(node);
          view.dispatch(transaction);

          // Move cursor to the right of the inserted image
          const resolvedPos = transaction.selection.$anchor.after();
          view.dispatch(
            view.state.tr.setSelection(
              TextSelection.create(view.state.doc, resolvedPos)
            )
          );
          view.focus();
        });

        return true;
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);

  useEffect(() => {
    if (autoFocus && editor && editor.isEditable) {
      editor.commands.focus("end");
    }
  }, [autoFocus, editor]);

  useEffect(() => {
    if (!editor) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "IMG") {
        setSelectedImageElement(target);
      } else {
        setSelectedImageElement(null);
      }
    };

    editor.view.dom.addEventListener("click", handleClick);
    return () => editor.view.dom.removeEventListener("click", handleClick);
  }, [editor]);

  //update user list when allUsers changes and editor is not null(Mention list is not rendered when editor is null)
  useEffect(() => {
    if (!editor || !allUsers || allUsers.length === 0) return;

    //Capitalize name
    const capitalize = (name: string) =>
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

    editor.storage.mention.userList = allUsers.map((user: any) => {
      const firstName = capitalize(user.first_name || "");
      const lastName = capitalize(user.last_name || "");
      const fullName = `${firstName} ${lastName}`;

      return {
        id: user.id,
        label: fullName,
        avatar:
          user.avatar_url ||
          `https://ui-avatars.com/api/?name=${firstName}+${lastName}`,
        fullName,
      };
    });
  }, [editor, allUsers]);

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        fontSize: "1rem",
        p: 1,
        "& .ProseMirror": {
          flex: 1,
          overflowY: "auto",
          p: 1,
          outline: "none",
          minHeight: "100%",
          whiteSpace: "pre-wrap", // ✅ Wrap text, respect newlines
          wordBreak: "break-word", // ✅ Break long words instead of stretching
          "&:focus": {
            outline: "none",
          },

          //-- Code block styling
          "& pre": {
            margin: "12px",
            backgroundColor: "#f4f5f7",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            position: "relative",
            fontFamily: "monospace",
            fontSize: "0.9rem",
            whiteSpace: "pre-wrap",
            minHeight: "50px",
          },

          "& code": {
            fontFamily: "monospace",
          },
        },

        // ✅ Placeholder handling
        "& .ProseMirror p.is-empty:first-of-type::before": {
          content: "attr(data-placeholder)",
          float: "left",
          color: "#aaa",
          pointerEvents: "none",
          height: 0,
        },
        "& .ProseMirror p.is-empty:first-of-type": {
          position: "relative",
        },
        "& .ProseMirror p.is-empty:not(:first-of-type)::before": {
          content: "''",
        },

        //-- Image styling
        "& .ProseMirror img": {
          width: "400px",
          maxHeight: "auto",
          objectFit: "contain",
          borderRadius: "4px",
          display: "block",
          marginTop: "8px",
          cursor: "pointer",
        },

        //-- Mention styling
        "& .mui-chip-mention": {
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 8px",
          backgroundColor: "#F7EEF9",
          color: "#7B1FA2",
          borderRadius: "16px",
          fontSize: "0.75rem",
          fontWeight: 500,
          border: "1px solid #F7EEF9",
          margin: "0 2px",
          cursor: "default",
          whiteSpace: "nowrap",
        },
        //-- Link styling
        "& .ProseMirror a": {
          color: "#7B1FA2", // standard MUI blue or your theme primary
          textDecoration: "underline",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      }}
    >
      <MenuBar editor={editor} />

      <EditorContent editor={editor} style={style} />
      {/* Image Popper Actions when select image from menu */}
      <Popper
        open={Boolean(selectedImageElement)}
        anchorEl={selectedImageElement}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <Paper sx={{ p: 1, display: "flex", gap: 1 }}>
          <Tooltip title="Delete Image">
            <IconButton
              size="small"
              onClick={() => {
                if (selectedImageElement && editor) {
                  const pos = editor.view.posAtDOM(selectedImageElement, 0);
                  editor
                    .chain()
                    .focus()
                    .deleteRange({ from: pos, to: pos + 1 })
                    .run();
                  setSelectedImageElement(null);
                }
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add/Edit Link">
            <IconButton
              size="small"
              onClick={() => {
                const currentLink =
                  selectedImageElement?.getAttribute("data-href") || "";
                const link = prompt("Enter link URL", currentLink);
                if (link && selectedImageElement) {
                  selectedImageElement.style.cursor = "pointer";
                  selectedImageElement.setAttribute("data-href", link);
                  selectedImageElement.onclick = () =>
                    window.open(link, "_blank");
                }
              }}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy Image URL">
            <IconButton
              color="secondary"
              size="small"
              onClick={async () => {
                if (selectedImageElement) {
                  const imageUrl = selectedImageElement.getAttribute("src");
                  if (!imageUrl) return;

                  try {
                    const response = await fetch(imageUrl);
                    const blob = await response.blob();

                    const clipboardItem = new ClipboardItem({
                      [blob.type]: blob,
                    });
                    await navigator.clipboard.write([clipboardItem]);
                  } catch (err) {
                    console.error("Failed to copy image:", err);
                  }
                }
              }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Paper>
      </Popper>
    </Box>
  );
};

export default TiptapEditor;
